import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FormAssignmentConfigDto,
  FormSectionAssignmentConfigBasicDto,
  UpdateFormAssignmentDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_QUESTIONS_ASSIGNMENT } from "../domain";
import { ACTION_GET_FORM_ASSIGNMENT_CONFIG } from "../GetFormAssignmentConfig";

import { questionsAssignmentApi } from "@api/services";
import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";

export const ACTION_UPDATE_FORM_ASSIGNMENT = "UPDATE_FORM_ASSIGNMENT";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

const updateFormAssignment = async (
  data: UpdateFormAssignmentDto,
): Promise<FormSectionAssignmentConfigBasicDto[]> => {
  const response =
    await questionsAssignmentApi.assignmentsConfigControllerUpdateFormAssignment(
      data,
    );

  return response.data;
};

export const useUpdateFormAssignment = (
  user: UserProfileDto | null,
  formId: string,
): {
  updateFormAssignment: (data: UpdateFormAssignmentDto) => void;
  updateFormAssignmentAsync: (
    data: UpdateFormAssignmentDto,
  ) => Promise<FormSectionAssignmentConfigBasicDto[]>;
  updateFormAssignmentEnabled: boolean;
  isSuccessUpdateFormAssignment: boolean;
  isPendingUpdateFormAssignment: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_QUESTIONS_ASSIGNMENT, ACTION_UPDATE_FORM_ASSIGNMENT],
    mutationFn: enabled ? updateFormAssignment : undefined,
    onSuccess: (data) => {
      const queryKey = [
        DOMAIN_QUESTIONS_ASSIGNMENT,
        ACTION_GET_FORM_ASSIGNMENT_CONFIG,
        {
          formId,
        },
      ];

      queryClient.setQueryData(
        queryKey,
        (currentData: FormAssignmentConfigDto | undefined) => {
          if (!currentData) return currentData;

          const dataMap = new Map(
            currentData.formSections.map((item) => [item.code, item]),
          );

          for (const updatedAssignment of data) {
            dataMap.set(updatedAssignment.code, updatedAssignment);
          }

          return {
            ...currentData,
            formSections: Array.from(dataMap.values()),
          };
        },
      );

      // Invalidate current form sections to refetch updated data
      queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            query.queryKey[0] === DOMAIN_FORMS &&
            query.queryKey[1] === ACTION_GET_FORM_SECTIONS
          );
        },
      });
    },
  });

  return {
    updateFormAssignment: mutate,
    updateFormAssignmentAsync: mutateAsync,
    updateFormAssignmentEnabled: enabled,
    isSuccessUpdateFormAssignment: isSuccess,
    isPendingUpdateFormAssignment: isPending,
  };
};

export const useIsMutatingUpdateFormAssignment = (): boolean => {
  return (
    useIsMutating({
      mutationKey: [DOMAIN_QUESTIONS_ASSIGNMENT, ACTION_UPDATE_FORM_ASSIGNMENT],
    }) > 0
  );
};
